"use client"

import React from 'react';
import { NavigationProps } from "@/types/navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Check, X, Sparkles, Zap, Shield, Rocket, BarChart4, Users2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface UpgradePageProps extends NavigationProps {}

interface Feature {
  name: string;
  free: boolean;
  pro: boolean;
  business: boolean;
}

export default function UpgradePage({ onNavigate }: UpgradePageProps) {
  const features: Feature[] = [
    { name: "Basic expense tracking", free: true, pro: true, business: true },
    { name: "Monthly reports", free: true, pro: true, business: true },
    { name: "Email support", free: true, pro: true, business: true },
    { name: "Up to 2 budgets", free: true, pro: true, business: true },
    { name: "Unlimited budgets", free: false, pro: true, business: true },
    { name: "Custom categories", free: false, pro: true, business: true },
    { name: "Priority support", free: false, pro: true, business: true },
    { name: "Advanced analytics", free: false, pro: true, business: true },
    { name: "Data export", free: false, pro: true, business: true },
    { name: "Team collaboration", free: false, pro: false, business: true },
    { name: "Role management", free: false, pro: false, business: true },
    { name: "API access", free: false, pro: false, business: true },
    { name: "Custom branding", free: false, pro: false, business: true },
    { name: "Dedicated support", free: false, pro: false, business: true },
  ];

  const plans = [
    {
      name: "Free",
      description: "Perfect for getting started",
      price: "$0",
      icon: Sparkles,
      features: features.filter(f => f.free),
      popular: false,
    },
    {
      name: "Pro",
      description: "Best for active budgeters",
      price: "$9.99",
      period: "month",
      icon: Zap,
      features: features.filter(f => f.pro),
      popular: true,
    },
    {
      name: "Business",
      description: "For teams and businesses",
      price: "$19.99",
      period: "month",
      icon: Rocket,
      features: features.filter(f => f.business),
      popular: false,
    },
  ];

  const benefits = [
    {
      title: "Advanced Analytics",
      description: "Get deeper insights into your spending patterns with detailed charts and predictions.",
      icon: BarChart4,
    },
    {
      title: "Priority Support",
      description: "Skip the queue with dedicated support from our expert team.",
      icon: Shield,
    },
    {
      title: "Team Collaboration",
      description: "Share and manage budgets with family members or team colleagues.",
      icon: Users2,
    },
  ];

  return (
    <>
      <PageHeader 
        onNavigate={onNavigate}
        currentPage="Upgrade to Pro"
        showDashboardLink={true}
      />

      <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
        <div className="mb-8 max-w-[980px] mx-auto text-center">
          <h1 className="text-3xl font-bold leading-tight tracking-tighter md:text-4xl mb-3">
            Upgrade to Pro
          </h1>
          <p className="text-lg text-muted-foreground">
            Choose the perfect plan for your budgeting needs
          </p>
        </div>

        {/* Benefits Section */}
        <div className="grid gap-6 md:grid-cols-3 mb-8 max-w-[980px] mx-auto">
          {benefits.map((benefit) => (
            <Card key={benefit.title} className="relative overflow-hidden">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <benefit.icon className="h-6 w-6 text-primary" />
                  <CardTitle className="text-xl">{benefit.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pricing Plans */}
        <div className="grid gap-6 md:grid-cols-3 mb-8 max-w-[980px] mx-auto">
          {plans.map((plan) => (
            <Card 
              key={plan.name} 
              className={cn(
                "flex flex-col",
                plan.popular && "border-primary shadow-md"
              )}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 mr-6 mt-4">
                  <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                    Popular
                  </span>
                </div>
              )}
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <plan.icon className="h-6 w-6 text-primary" />
                  <CardTitle>{plan.name}</CardTitle>
                </div>
                <CardDescription>{plan.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  {plan.period && (
                    <span className="text-muted-foreground">/{plan.period}</span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature.name} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm">{feature.name}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full" 
                  variant={plan.popular ? "default" : "outline"}
                  onClick={() => onNavigate('billing')}
                >
                  {plan.name === "Free" ? "Get Started" : `Upgrade to ${plan.name}`}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Feature Comparison Table */}
        <Card className="max-w-[980px] mx-auto">
          <CardHeader>
            <CardTitle>Feature Comparison</CardTitle>
            <CardDescription>Detailed comparison of all features across plans</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="w-full overflow-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-4">Feature</th>
                    <th className="text-center py-4">Free</th>
                    <th className="text-center py-4">Pro</th>
                    <th className="text-center py-4">Business</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((feature) => (
                    <tr key={feature.name} className="border-b last:border-0">
                      <td className="py-4">{feature.name}</td>
                      <td className="text-center py-4">
                        {feature.free ? (
                          <Check className="h-4 w-4 text-primary mx-auto" />
                        ) : (
                          <X className="h-4 w-4 text-muted-foreground mx-auto" />
                        )}
                      </td>
                      <td className="text-center py-4">
                        {feature.pro ? (
                          <Check className="h-4 w-4 text-primary mx-auto" />
                        ) : (
                          <X className="h-4 w-4 text-muted-foreground mx-auto" />
                        )}
                      </td>
                      <td className="text-center py-4">
                        {feature.business ? (
                          <Check className="h-4 w-4 text-primary mx-auto" />
                        ) : (
                          <X className="h-4 w-4 text-muted-foreground mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>
    </>
  );
} 